import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { debug } from '@/utils/debug';
import styles from './GoogleAdsLinker.module.css';

// Validation schema for customer ID
const customerIdSchema = z.object({
  customerId: z
    .string()
    .min(1, 'Customer ID is required')
    .regex(/^\d{3}-?\d{3}-?\d{4}$/, 'Customer ID must be in format XXX-XXX-XXXX or XXXXXXXXXX')
    .transform((val) => val.replace(/-/g, '')), // Remove hyphens for API
});

type CustomerIdForm = z.infer<typeof customerIdSchema>;

interface GoogleAdsLinkerProps {
  isOpen: boolean;
  onClose: () => void;
  onConnect: (customerId: string) => void;
  isConnecting?: boolean;
}

export function GoogleAdsLinker({ 
  isOpen, 
  onClose, 
  onConnect, 
  isConnecting = false 
}: GoogleAdsLinkerProps) {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formData, setFormData] = useState<CustomerIdForm | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
    reset,
  } = useForm<CustomerIdForm>({
    resolver: zodResolver(customerIdSchema),
    mode: 'onChange',
  });

  const currentCustomerId = watch('customerId');

  // Format customer ID as user types
  const handleCustomerIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    
    // Limit to 10 digits
    if (value.length > 10) {
      value = value.slice(0, 10);
    }
    
    // Add hyphens for display
    if (value.length >= 6) {
      value = `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`;
    } else if (value.length >= 3) {
      value = `${value.slice(0, 3)}-${value.slice(3)}`;
    }
    
    setValue('customerId', value, { shouldValidate: true });
  };

  const onSubmit = (data: CustomerIdForm) => {
    debug.log('GoogleAdsLinker: Customer ID submitted', data.customerId);
    setFormData(data);
    setShowConfirmation(true);
  };

  const handleConfirmConnection = () => {
    if (!formData) return;
    
    debug.log('GoogleAdsLinker: Confirming connection for', formData.customerId);
    onConnect(formData.customerId);
    // Don't call handleClose() - let PollingManager handle the state transition
  };

  const handleClose = useCallback(() => {
    debug.log('GoogleAdsLinker: Closing modal');
    setShowConfirmation(false);
    setFormData(null);
    reset();
    onClose();
  }, [onClose, reset]);

  const handleBackToForm = () => {
    setShowConfirmation(false);
    setFormData(null);
  };

  // Handle Escape key to close modal
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && !isConnecting) {
      handleClose();
    }
  }, [isConnecting, handleClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const modalTitleId = 'google-ads-linker-title';

  return (
    <div
      className={styles.modalOverlay}
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={modalTitleId}
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 id={modalTitleId} className={styles.modalTitle}>Link Google Ads</h3>
          <button
            className={styles.modalClose}
            onClick={handleClose}
            disabled={isConnecting}
            aria-label="Close dialog"
          >
            &times;
          </button>
        </div>

        {!showConfirmation ? (
          // Customer ID Input Form
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label htmlFor="customerId" className={styles.formLabel}>
                  Google Ads Customer ID
                </label>
                <input
                  {...register('customerId')}
                  type="text"
                  id="customerId"
                  className={`${styles.formInput} ${errors.customerId ? styles.error : ''}`}
                  placeholder="123-456-7890"
                  maxLength={12}
                  onChange={handleCustomerIdChange}
                  disabled={isConnecting}
                />
                {errors.customerId && (
                  <div className={styles.formError}>
                    {errors.customerId.message}
                  </div>
                )}
                <div className={styles.formHelp}>
                  Your Customer ID can be found in the upper right-hand corner of your Google Ads console. 
                  It appears as a 10-digit number in the format XXX-XXX-XXXX.
                </div>
              </div>
            </div>
            
            <div className={styles.modalFooter}>
              <button 
                type="button" 
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={handleClose}
                disabled={isConnecting}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className={`${styles.btn} ${styles.btnPrimary}`}
                disabled={!isValid || isConnecting}
              >
                {isConnecting ? 'Connecting...' : 'Submit Account ID'}
              </button>
            </div>
          </form>
        ) : (
          // Confirmation Step
          <div>
            <div className={styles.modalBody}>
              <div className={`${styles.alert} ${styles.alertWarning}`}>
                <div className={styles.alertIcon}>⚠️</div>
                <div className={styles.alertContent}>
                  <div className={styles.alertTitle}>Confirm Account Connection</div>
                  <div className={styles.alertText}>
                    You're about to connect Google Ads Customer ID: <strong>{currentCustomerId}</strong>
                    <br /><br />
                    By proceeding, you agree to our <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}><strong>Terms of Service</strong></a> and authorize Flouri to access your Google Ads account data for optimization purposes.
                  </div>
                </div>
              </div>
            </div>
            
            <div className={styles.modalFooter}>
              <button 
                type="button" 
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={handleBackToForm}
                disabled={isConnecting}
              >
                Back
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={handleConfirmConnection}
                disabled={isConnecting}
              >
                {isConnecting ? 'Connecting...' : 'Confirm Connection'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}